export const APP_NAME = 'Tally';
export const APP_TAGLINE = 'Verified talent for modern hiring.';

export const ROLES = {
  candidate: 'candidate',
  employer: 'employer',
  admin: 'admin',
} as const;
export type Role = (typeof ROLES)[keyof typeof ROLES];

export const PRIVACY_LEVELS = {
  public: 'public',
  recruiters_only: 'recruiters_only',
  private: 'private',
} as const;
export type PrivacyLevel = (typeof PRIVACY_LEVELS)[keyof typeof PRIVACY_LEVELS];

export const PRIVACY_LABEL: Record<PrivacyLevel, string> = {
  public: 'Public',
  recruiters_only: 'Recruiters only',
  private: 'Private',
};

export const VERIFICATION_STATUS_LABEL: Record<string, string> = {
  pending: 'Pending',
  verified: 'Verified',
  rejected: 'Rejected',
  disqualified: 'Disqualified',
  insufficient_data: 'Insufficient data',
};

export const ROUTES = {
  home: '/',
  login: '/login',
  signup: '/signup',
  forgotPassword: '/forgot-password',
  roleSelection: '/role-selection',
  oauthCallback: '/oauth/callback',
  welcome: '/welcome',
  completeProfile: '/complete-profile',
  publicProfile: (slug: string) => `/public/${slug}`,
  publicCaseLookup: '/public/case-lookup',
  onboardingStatusOutage: '/onboarding-unavailable',

  dashboard: '/dashboard',
  profile: '/profile',
  profileSettings: '/profile/settings',
  messaging: '/messaging',
  notifications: '/notifications',
  network: '/network',
  jobs: '/jobs',
  settings: '/settings',
  aiExtractReview: '/ai-extract/review',
  nftCaseDetail: (id: string | number) => `/nft/${id}`,

  candidateVerification: '/candidate/verification',
  candidateVerificationPricing: '/candidate/verification/pricing',
  candidateVerificationOtp: '/candidate/verification/otp',
  candidateVerificationRecords: '/candidate/verification/records',
  candidateVerificationConsent: '/candidate/verification/consent',
  candidateVerificationConfirm: '/candidate/verification/confirm',

  employer: '/employer',
  employerDiscovery: '/employer/candidate-discovery',
  employerMessaging: '/employer/messaging',
  employerSettings: '/employer/settings',
  employerCandidatePreview: (slug: string) => `/employer/candidate-preview/${slug}`,
  // Employer self-verification (verifies their own company profile)
  employerVerification: '/employer/verification',
  employerVerificationPricing: '/employer/verification/pricing',
  employerVerificationOtp: '/employer/verification/otp',
  employerVerificationRecords: '/employer/verification/records',
  employerVerificationConsent: '/employer/verification/consent',
  employerVerificationConfirm: '/employer/verification/confirm',

  // Employer verification requests (case builder for candidate profiles)
  employerVerificationRequests: '/employer/verification-requests',
  employerVerificationRequestCase: (id: string | number) =>
    `/employer/verification-requests/cases/${id}`,
  employerVerificationRequestsNew: '/employer/verification-requests/new/pricing',
  employerVerificationRequestsNewPricing: '/employer/verification-requests/new/pricing',
  employerVerificationRequestsNewOtp: '/employer/verification-requests/new/otp',
  employerVerificationRequestsNewBuilder: '/employer/verification-requests/new/case-builder',
  employerVerificationRequestsNewConsent: '/employer/verification-requests/new/consent',
  employerVerificationRequestsNewConfirm: '/employer/verification-requests/new/confirm',

  admin: '/admin',
  adminLogin: '/admin/login',
  adminUsers: '/admin/user-details',
  adminUserDetail: (userId: string | number) => `/admin/user-details/${userId}`,
  adminManagement: '/admin/admins',
  adminReports: '/admin/reports',
  adminSettings: '/admin/settings',
  adminVerifications: '/admin/verifications',
  adminVerificationCase: (id: string | number) => `/admin/verification-cases/${id}`,
  adminVerificationTask: (caseId: string | number, taskId: string | number) =>
    `/admin/verification-cases/${caseId}/tasks/${taskId}`,
} as const;

export const STORAGE_KEYS = {
  authTokens: 'tally:auth:tokens',
  adminAuthTokens: 'tally:auth:admin-tokens',
  locale: 'tally:i18n:locale',
  theme: 'tally:ui:theme',
  cookieConsent: 'tally:cookie-consent',
} as const;
